// ClienteSetPassword - Página pública para que el cliente active su cuenta creando una contraseña
// Ruta: /clientes/activar/:token

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { clientOnboardingService } from '@/services/clientOnboardingService';
import TokenStorage from '@/utils/tokenStorage';
import {
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

export function ClienteSetPassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('portal');

  // Token validation state
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [clientName, setClientName] = useState<string | null>(null);
  const [clientEmail, setClientEmail] = useState<string | null>(null);

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }

    const validate = async () => {
      try {
        const result = await clientOnboardingService.validateToken(token);
        setTokenValid(result.valid);
        setClientName(result.client_name);
        setClientEmail(result.email);
      } catch {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validate();
  }, [token]);

  // Password validation
  const passwordMinLength = 8;
  const passwordsMatch = password === confirmPassword;
  const passwordLongEnough = password.length >= passwordMinLength;
  const canSubmit = passwordLongEnough && passwordsMatch && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !token) return;

    setError(null);
    setSubmitting(true);

    try {
      const result = await clientOnboardingService.setPassword(token, password);

      // Auto-login: save tokens
      TokenStorage.saveTokens({
        access_token: result.access_token,
        refresh_token: result.refresh_token,
        token_type: result.token_type,
        expires_in: result.expires_in,
        refresh_expires_in: result.refresh_expires_in,
      });

      // Navigate to onboarding
      navigate('/clientes/onboarding', { replace: true });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(detail || t('onboarding.setPassword.error', 'Error al activar la cuenta. Inténtalo de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="h-8 w-8 text-green-600 animate-spin" />
          <p className="text-gray-600 dark:text-gray-400">
            {t('onboarding.setPassword.validating', 'Verificando enlace...')}
          </p>
        </div>
      </div>
    );
  }

  // Invalid/expired token
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="max-w-md w-full shadow-xl">
            <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950 dark:to-orange-950">
              <CardTitle className="text-xl text-center text-red-700 dark:text-red-400 flex items-center justify-center gap-2">
                <ExclamationTriangleIcon className="h-6 w-6" />
                {t('onboarding.setPassword.invalidTitle', 'Enlace no válido')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 text-center space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                {t('onboarding.setPassword.invalidDesc', 'Este enlace de invitación ha expirado o ya fue utilizado.')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {t('onboarding.setPassword.invalidHelp', 'Contacta con tu abogado para solicitar un nuevo enlace de activación.')}
              </p>
              <div className="pt-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/clientes')}
                >
                  {t('onboarding.setPassword.goToPortal', 'Ir al portal')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Valid token — show password form
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-md w-full"
      >
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950">
            <div className="text-center space-y-2">
              <div className="mx-auto w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <LockClosedIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-xl text-gray-900 dark:text-gray-100">
                {t('onboarding.setPassword.title', 'Activa tu cuenta')}
              </CardTitle>
              {clientName && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('onboarding.setPassword.greeting', 'Hola')}, <strong>{clientName}</strong>
                </p>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {t('onboarding.setPassword.subtitle', 'Crea una contraseña para acceder a tu Área de Clientes')}
              </p>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email (readonly info) */}
              {clientEmail && (
                <div>
                  <Label htmlFor="email">{t('onboarding.setPassword.email', 'Email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={clientEmail}
                    disabled
                    className="mt-1 bg-gray-50 dark:bg-gray-800"
                  />
                </div>
              )}

              {/* Password */}
              <div>
                <Label htmlFor="password">
                  {t('onboarding.setPassword.password', 'Contraseña')} <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(null); }}
                    placeholder={t('onboarding.setPassword.passwordPlaceholder', 'Mínimo 8 caracteres')}
                    disabled={submitting}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {password.length > 0 && !passwordLongEnough && (
                  <p className="text-xs text-red-500 mt-1">
                    {t('onboarding.setPassword.tooShort', 'La contraseña debe tener al menos 8 caracteres')}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirmPassword">
                  {t('onboarding.setPassword.confirmPassword', 'Confirmar contraseña')} <span className="text-red-500">*</span>
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
                    placeholder={t('onboarding.setPassword.confirmPlaceholder', 'Repite la contraseña')}
                    disabled={submitting}
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-xs text-red-500 mt-1">
                    {t('onboarding.setPassword.noMatch', 'Las contraseñas no coinciden')}
                  </p>
                )}
              </div>

              {/* Password strength indicators */}
              {password.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    <CheckCircleIcon className={`h-4 w-4 ${passwordLongEnough ? 'text-green-500' : 'text-gray-300'}`} />
                    <span className={passwordLongEnough ? 'text-green-600' : 'text-gray-400'}>
                      {t('onboarding.setPassword.rule8chars', 'Al menos 8 caracteres')}
                    </span>
                  </div>
                  {confirmPassword.length > 0 && (
                    <div className="flex items-center gap-2 text-xs">
                      <CheckCircleIcon className={`h-4 w-4 ${passwordsMatch ? 'text-green-500' : 'text-gray-300'}`} />
                      <span className={passwordsMatch ? 'text-green-600' : 'text-gray-400'}>
                        {t('onboarding.setPassword.ruleMatch', 'Las contraseñas coinciden')}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                className="w-full"
                disabled={!canSubmit || submitting}
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    {t('onboarding.setPassword.activating', 'Activando...')}
                  </span>
                ) : (
                  t('onboarding.setPassword.submit', 'Activar mi cuenta')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
